import { useState, useEffect, useMemo, useCallback } from 'react';
import AppLayout from '../../layouts/AppLayout';
import QuestionCodeEditor from '../../components/XmlEditor/QuestionCodeEditor';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Chip,
  Snackbar,
  Alert,
  Skeleton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBackIosNew } from '@mui/icons-material';
import { fetchData, postData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import { getQuestionTypeName } from '../../types/questionTypeMap';
import { LoadingIndicator } from '../../components/LoadingIndicator';

interface QuestionDetails {
  questionId: string;
  questionType: string;
  productId: string;
  productTitle: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const XMLEditortContainer: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingQuestionXML, setLoadingQuestionXML] = useState(false);
  const { jiraId, questionId } = useParams<{
    jiraId: string;
    questionId: string;
  }>();
  const [questionDetails, setQuestionDetails] = useState<QuestionDetails | null>(
    null
  );
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const [originalXml, setOriginalXml] = useState('');
  const [currentXml, setCurrentXml] = useState('');

  const hasChanges = useMemo(() => {
    return originalXml.trim() !== currentXml.trim();
  }, [originalXml, currentXml]);

  const handleXmlChange = (value: string) => {
    setCurrentXml(value);
  };

  const navigateBack = () => {
    navigate(-1);
  };

  const saveQuestionXML = async () => {
    console.log('Saving question XML');

    setLoadingQuestionXML(true);
    try {
      const validationResult = validateXML(currentXml);
      if (!validationResult?.valid) {
        setSnackbar({
          open: true,
          message: 'Invalid XML. Please fix formatting errors before saving.',
          severity: 'error',
        });
        return;
      }
      const url = `/questions/${questionDetails?.questionId}/jira/${jiraId}/xml`;
      const body = {
        xml: currentXml,
      };
      const response = await postData(
        url,
        PERMISSIONS.SAVE_QUESTION_XML,
        body
      );
      if (response) {
        console.log('Question XML saved successfully');
        // After saving, update original to current
        setOriginalXml(currentXml);
        setSnackbar({
          open: true,
          message: 'Question XML saved successfully',
          severity: 'success',
        });
      } else {
        throw new Error('No response received. Please Try again later.');
      }
    } catch (error) {
      console.error('Error saving question XML:', error);
      setSnackbar({
        open: true,
        message: 'Question XML save failed',
        severity: 'error',
      });
    } finally {
      setLoadingQuestionXML(false);
    }
  };

  const validateXML = (xmlString: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "application/xml");
    const parserError = doc.getElementsByTagName("parsererror");
    if (parserError.length > 0) {
      return {
        valid: false,
        error: parserError[0].textContent,
      };
    }
    return { valid: true };
  }

  const loadQuestionXML = useCallback(async () => {
    setLoading(true);
    if (!questionId || !jiraId) {
      setLoading(false);
      setSnackbar({
        open: true,
        message: 'Question ID or JIRA ID is missing',
        severity: 'error',
      });
      return;
    }
    try {
      const url = `/questions/${questionId}/jira/${jiraId}/xml`;
      const response = await fetchData(url, PERMISSIONS.GET_QUESTION_XML);

      // Extract questionXml from response (the API returns it as questionXml)
      const xml = response?.questionXml;
      setOriginalXml(xml);
      setCurrentXml(xml);
    } catch (error) {
      console.error('Error fetching question XML:', error);
      // Fallback to sample XML on error
      setOriginalXml('');
      setCurrentXml('');
      setSnackbar({
        open: true,
        message: 'Error fetching question XML',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [questionId, jiraId]);

  const fetchIssueData = useCallback(async () => {
    if (!jiraId || !questionId) return;

    setLoading(true);
    try {
      const url = `/issues?jiraId=${jiraId}&masterQuestionId=${questionId}`;
      const respone = await fetchData(url, PERMISSIONS.GET_ISSUE)
      const issue = respone.issue;
      const product = respone.productDetails;
      const question = respone.questionDetails;
      setQuestionDetails({
        questionId: issue.dataMasterQuestionId,
        questionType: getQuestionTypeName(question.nTypeId),
        productId: product.productUuid,
        productTitle: product.title,
      });
      loadQuestionXML();
    } catch (e: any) {
      console.error('Error fetching question XML:', e);
      setSnackbar({
        open: true,
        message: 'Error fetching question Data!',
        severity: 'error',
      });
    }
  }, [jiraId, questionId, loadQuestionXML]);

  useEffect(() => {
    fetchIssueData();
  }, [fetchIssueData]);

  if (loading) {
    // Skeleton loader for the card
    return (
      <AppLayout>
        <div style={{ padding: '16px' }}>
          <Skeleton variant="text" width={200} height={48} sx={{ mb: 3 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mb: 3,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={120} height={24} />
              </Box>
              <Box>
                <Skeleton variant="text" width={100} height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={150} height={24} />
              </Box>
              <Box>
                <Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={100} height={24} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Skeleton variant="text" width={90} height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={120} height={24} />
              </Box>
              <Box>
                <Skeleton variant="text" width={100} height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={120} height={24} />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="rectangular" width={150} height={32} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 1 }} />
          </Box>
        </div>

        <Snackbar
          open={snackbar?.open}
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar?.severity} sx={{ width: '100%' }}>
            {snackbar?.message}
          </Alert>
        </Snackbar>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ paddingLeft: '16px', paddingRight: '16px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4">
            <IconButton onClick={navigateBack}>
              <ArrowBackIosNew fontSize="small" />
            </IconButton>
            XML Editor
          </Typography>
        </Box>
        <Typography variant="h3" sx={{ mb: 3, fontWeight: 500 }}>
          Issue Details
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mb: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                JIRA ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {jiraId}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Product Title
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {questionDetails?.productTitle}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Product ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {questionDetails?.productId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Question ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {questionDetails?.questionId}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Question Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {questionDetails?.questionType}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={
                hasChanges ? 'Please Save Changes' : 'No Changes Detected'
              }
              color={hasChanges ? 'warning' : 'default'}
              size="small"
              sx={{
                fontWeight: hasChanges ? 500 : 400,
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={saveQuestionXML}
              disabled={!hasChanges}
            >
              Save
            </Button>
          </Box>
        </Box>
        {loadingQuestionXML && (
          <LoadingIndicator />
        )}
        <div style={{ marginTop: '16px', marginBottom: '25px' }}>
          <QuestionCodeEditor
            code={currentXml}
            onValueChange={handleXmlChange}
          />
        </div>
      </div>

      <Snackbar
        open={snackbar?.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
};

export default XMLEditortContainer;
